/**
 * Date and time utilities for MS SQL Server compatibility
 * Migrated from PostgreSQL to MS SQL Server date handling
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Extend dayjs with required plugins
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Format date for MS SQL Server API (DateTime format)
 * MS SQL Server expects DateTime in format: YYYY-MM-DDTHH:mm:ss.sss
 * or ISO 8601 format for better compatibility
 */
export const formatDateForApi = (dateString: string | Date | null | undefined): string => {
    if (!dateString) return '';

    try {
        let date: dayjs.Dayjs;

        if (typeof dateString === 'string') {
            // Handle YYYY-MM-DD format (from HTML date inputs).
            // For date-only fields (no time component) we must NOT convert to
            // UTC — doing so shifts the date backward by the local UTC offset
            // (e.g. AEST +10 turns 2000-05-31 into 2000-05-30T14:00:00Z).
            // Return a plain local-midnight string so SQL Server stores the
            // correct calendar date regardless of server timezone.
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
                date = dayjs(dateString);
                if (!date.isValid()) {
                    console.warn('Invalid date provided to formatDateForApi:', dateString);
                    return '';
                }
                return date.format('YYYY-MM-DDT00:00:00');
            } else {
                date = dayjs(dateString);
            }
        } else {
            date = dayjs(dateString);
        }

        if (!date.isValid()) {
            console.warn('Invalid date provided to formatDateForApi:', dateString);
            return '';
        }

        // Return ISO string format for MS SQL Server compatibility
        return date.toISOString();
    } catch (error) {
        console.error('Error formatting date for API:', error, dateString);
        return '';
    }
};

/**
 * Format datetime for MS SQL Server API
 * Used for datetime fields with time components
 * Ensures the datetime is converted to UTC before serialization
 */
export const formatDateTimeForApi = (datetime: string | Date | dayjs.Dayjs | null | undefined): string => {
    if (!datetime) return '';

    try {
        let date = dayjs(datetime);
        if (!date.isValid()) {
            console.warn('Invalid datetime provided to formatDateTimeForApi:', datetime);
            return '';
        }

        // Convert to UTC before returning ISO string
        return date.utc().toISOString();
    } catch (error) {
        console.error('Error formatting datetime for API:', error, datetime);
        return '';
    }
};

/**
 * Format date received from MS SQL Server API for display
 * MS SQL Server returns dates in ISO format or DateTime format
 */
export const formatDateForDisplay = (dateString: string | null | undefined, format: string = 'MMM DD, YYYY'): string => {
    if (!dateString) return '';

    try {
        const date = dayjs(dateString);
        if (!date.isValid()) {
            return 'Invalid Date';
        }
        return date.format(format);
    } catch (error) {
        console.error('Error formatting date for display:', error, dateString);
        return 'Invalid Date';
    }
};

/**
 * Get the user's current timezone
 */
export const getUserTimezone = (): string => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Format datetime received from MS SQL Server API for display with timezone awareness.
 *
 * Times are written to the backend in UTC (`formatDateTimeForApi` uses
 * `.utc().toISOString()`), but EF + SQL Server `DateTime` columns return as
 * `Kind=Unspecified`, so the JSON has no `Z` marker. Without forcing dayjs to
 * treat the bare string as UTC it would parse as local time — making a 01:00
 * UTC value show as 01:00 Melbourne instead of 11:00. We use `dayjs.utc()` so
 * Z-suffixed and bare strings are both interpreted as UTC, then converted to
 * the user's local zone.
 */
export const formatDateTimeForDisplay = (datetimeString: string | null | undefined, format: string = 'MMM DD, YYYY HH:mm'): string => {
    if (!datetimeString) return '';

    try {
        const userTimezone = getUserTimezone();
        const datetime = dayjs.utc(datetimeString).tz(userTimezone);
        if (!datetime.isValid()) {
            return 'Invalid DateTime';
        }
        return datetime.format(format);
    } catch (error) {
        console.error('Error formatting datetime for display:', error, datetimeString);
        return 'Invalid DateTime';
    }
};

/**
 * Format datetime for user's local timezone with proper timezone conversion
 */
export const formatDateTimeForUserTimezone = (datetimeString: string | null | undefined): string => {
    if (!datetimeString || datetimeString === '0001-01-01T00:00:00') return 'Not set';

    try {
        // See `formatDateTimeForDisplay` for the same UTC-parsing rationale.
        const userTimezone = getUserTimezone();
        const datetime = dayjs.utc(datetimeString).tz(userTimezone);
        if (!datetime.isValid()) {
            return 'Invalid date';
        }

        // Format in Australian style: DD/MM/YYYY HH:mm AM/PM
        return datetime.format('DD/MM/YYYY hh:mm A');
    } catch (error) {
        console.error('Error formatting date for Australia:', error, datetimeString);
        return 'Invalid date';
    }
};

/**
 * Format time received from MS SQL Server API for display
 * MS SQL Server returns TimeSpan as string like "09:30:00" or datetime with time component
 */
export const formatTimeForDisplay = (timeString: string | null | undefined, format: string = 'HH:mm'): string => {
    if (!timeString) return '';

    try {
        // Handle TimeSpan format (HH:mm:ss)
        if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(timeString)) {
            const time = dayjs(`1970-01-01T${timeString}`);
            if (time.isValid()) {
                return time.format(format);
            }
        }

        // Handle full datetime string
        const time = dayjs(timeString);
        if (time.isValid()) {
            return time.format(format);
        }

        return timeString; // Return as-is if parsing fails
    } catch (error) {
        console.error('Error formatting time for display:', error, timeString);
        return timeString || '';
    }
};

/**
 * Format time for MS SQL Server API
 * Converts time to TimeSpan format (HH:mm:ss) expected by MS SQL Server
 */
export const formatTimeForApi = (time: dayjs.Dayjs | string | null | undefined): string => {
    if (!time) return '';

    try {
        let timeValue: dayjs.Dayjs;

        if (typeof time === 'string') {
            // If already in HH:mm:ss format, return as-is
            if (/^\d{1,2}:\d{2}:\d{2}$/.test(time)) {
                return time;
            }
            timeValue = dayjs(time);
        } else {
            timeValue = time;
        }

        if (!timeValue.isValid()) {
            console.warn('Invalid time provided to formatTimeForApi:', time);
            return '';
        }

        // Return in HH:mm:ss format for MS SQL Server TimeSpan
        return timeValue.format('HH:mm:ss');
    } catch (error) {
        console.error('Error formatting time for API:', error, time);
        return '';
    }
};

/**
 * Parse date string for form input (YYYY-MM-DD format)
 * Used when populating form fields from API data
 */
export const formatDateForInput = (dateString: string | null | undefined): string => {
    if (!dateString) return '';

    try {
        const date = dayjs(dateString);
        if (!date.isValid()) {
            return '';
        }
        return date.format('YYYY-MM-DD');
    } catch (error) {
        console.error('Error formatting date for input:', error, dateString);
        return '';
    }
};

/**
 * Parse datetime string for form input (YYYY-MM-DDTHH:mm format)
 * Used when populating datetime-local inputs from API data
 */
export const formatDateTimeForInput = (datetimeString: string | null | undefined): string => {
    if (!datetimeString) return '';

    try {
        const datetime = dayjs(datetimeString);
        if (!datetime.isValid()) {
            return '';
        }
        return datetime.format('YYYY-MM-DDTHH:mm');
    } catch (error) {
        console.error('Error formatting datetime for input:', error, datetimeString);
        return '';
    }
};

/**
 * Calculate age from birth date
 * Handles MS SQL Server date format
 */
export const calculateAge = (dateOfBirth: string | null | undefined): number => {
    if (!dateOfBirth) return 0;

    try {
        const birthDate = dayjs(dateOfBirth);
        const today = dayjs();

        if (!birthDate.isValid()) {
            return 0;
        }

        return today.diff(birthDate, 'year');
    } catch (error) {
        console.error('Error calculating age:', error, dateOfBirth);
        return 0;
    }
};

/**
 * Validate if a date string is valid
 */
export const isValidDate = (dateString: string | null | undefined): boolean => {
    if (!dateString) return false;
    
    try {
        const date = dayjs(dateString);
        return date.isValid();
    } catch (error) {
        return false;
    }
};

/**
 * Get current date in API format
 */
export const getCurrentDateForApi = (): string => {
    return dayjs().toISOString();
};

/**
 * Get current date in input format (YYYY-MM-DD)
 */
export const getCurrentDateForInput = (): string => {
    return dayjs().format('YYYY-MM-DD');
};

/**
 * Convert UTC date from MS SQL Server to local timezone
 */
export const convertUtcToLocal = (utcDateString: string | null | undefined): dayjs.Dayjs | null => {
    if (!utcDateString) return null;

    try {
        return dayjs.utc(utcDateString).local();
    } catch (error) {
        console.error('Error converting UTC to local:', error, utcDateString);
        return null;
    }
};

/**
 * Convert local date to UTC for MS SQL Server
 */
export const convertLocalToUtc = (localDateString: string | Date | dayjs.Dayjs | null | undefined): string => {
    if (!localDateString) return '';

    try {
        return dayjs(localDateString).utc().toISOString();
    } catch (error) {
        console.error('Error converting local to UTC:', error, localDateString);
        return '';
    }
};

/**
 * Format duration in hours
 */
export const formatDuration = (hours: number | null | undefined): string => {
    if (!hours && hours !== 0) return '';
    
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    
    if (m === 0) {
        return `${h}h`;
    }
    
    return `${h}h ${m}m`;
};

/**
 * Parse TimeSpan string from MS SQL Server (HH:mm:ss) to minutes
 */
export const parseTimeSpanToMinutes = (timeSpan: string | null | undefined): number => {
    if (!timeSpan) return 0;

    try {
        const parts = timeSpan.split(':');
        if (parts.length >= 2) {
            const hours = parseInt(parts[0], 10);
            const minutes = parseInt(parts[1], 10);
            return (hours * 60) + minutes;
        }
        return 0;
    } catch (error) {
        console.error('Error parsing TimeSpan to minutes:', error, timeSpan);
        return 0;
    }
};
